import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      icon: <Shield className="h-8 w-8 text-blue-500" />,
      title: "Information We Collect",
      content: [
        "Personal information you provide (name, email, phone number)",
        "Contact information for service inquiries",
        "Website usage data and analytics",
        "Communication preferences and service history"
      ]
    },
    {
      icon: <Lock className="h-8 w-8 text-green-500" />,
      title: "How We Use Your Information",
      content: [
        "To provide safety net installation services",
        "To respond to your inquiries and requests",
        "To improve our website and services",
        "To send relevant safety information and updates"
      ]
    },
    {
      icon: <Eye className="h-8 w-8 text-purple-500" />,
      title: "Information Sharing",
      content: [
        "We do not sell your personal information to third parties",
        "We may share information with trusted service providers",
        "We may disclose information if required by law",
        "We protect your information with industry-standard security"
      ]
    },
    {
      icon: <Database className="h-8 w-8 text-orange-500" />,
      title: "Data Security",
      content: [
        "We use SSL encryption for data transmission",
        "Regular security audits and updates",
        "Limited access to personal information",
        "Secure storage of customer data"
      ]
    },
    {
      icon: <UserCheck className="h-8 w-8 text-red-500" />,
      title: "Your Rights",
      content: [
        "Access your personal information",
        "Request correction of inaccurate data",
        "Request deletion of your information",
        "Opt-out of marketing communications"
      ]
    },
    {
      icon: <FileText className="h-8 w-8 text-indigo-500" />,
      title: "Contact Us",
      content: [
        "For privacy concerns, contact us at info@jarasafetynets.com",
        "Call us at +91 8466 055 207",
        "Visit our office at Karmanghat, Hyderabad",
        "We respond to privacy requests within 30 days"
      ]
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('/images/building-nets.webp')`
        }}></div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gray-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Your privacy is important to us. Learn how we collect, use, and protect your personal information.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-gray-300 mt-4"
          >
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Our Commitment to Your Privacy
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              At Jara Safety Nets, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or use our safety net installation services.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              By using our website and services, you consent to the data practices described in this policy. If you do not agree 
              with the terms of this Privacy Policy, please do not use our website or services.
            </p>
          </motion.div>

          {/* Privacy Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center space-x-3 mb-4">
                  {section.icon}
                  <h3 className="text-xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-blue-50 rounded-xl p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Additional Information
            </h3>
            <div className="space-y-4 text-gray-600">
              <p>
                <strong>Cookies:</strong> We use cookies to enhance your browsing experience and analyze website traffic. 
                You can control cookie settings through your browser preferences.
              </p>
              <p>
                <strong>Third-Party Links:</strong> Our website may contain links to third-party websites. We are not responsible 
                for the privacy practices of these external sites.
              </p>
              <p>
                <strong>Policy Updates:</strong> We may update this Privacy Policy from time to time. We will notify you of any 
                significant changes by posting the new policy on our website.
              </p>
              <p>
                <strong>Children's Privacy:</strong> Our services are not directed to children under 13. We do not knowingly 
                collect personal information from children under 13.
              </p>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-8 text-center text-white"
          >
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Questions About Our Privacy Policy?
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              If you have any questions about this Privacy Policy or our data practices, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@jarasafetynets.com"
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <FileText className="mr-2 h-5 w-5" />
                Email Us
              </a>
              <a
                href="tel:+918466055207"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
              >
                <Shield className="mr-2 h-5 w-5" />
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
